<template>
  <AnnotationToolViewingMode3DControls
    :show="true"
    @top-view="handleTopView"
    @front-view="handleFrontView"
    @perspective-camera="switchToPerspectiveCamera"
    @orthographic-camera="switchToOrthographicCamera"
  />
  <TresCanvas
    ref="canvas"
    v-bind="gl"
    render-mode="on-demand"
  >
    <TresPerspectiveCamera
      v-if="cameraType === 'perspective'"
      ref="cameraPerspective"
      :position="cameraPerspectivePosition"
      :up="upDirection"
      :fov="45"
      :look-at="lookAt"
    />
    <TresOrthographicCamera
      v-else-if="cameraType === 'orthographic'"
      ref="cameraOrthographic"
      :up="upDirection"
      :position="cameraOrthographicPosition"
    />
    <OrbitControls
      ref="controls"
      make-default
      :enableDamping="true"
      :dampingFactor="1"
    />
    <TransformControls
      v-if="transformEnabled"
      ref="transformControls"
      :object="transformObject"
      :mode="transformMode"
      @mouse-up="handleTransformMouseUp(transformObject)"
    />
    <TresGroup v-if="showTypes?.pcd" ref="pointArrayGroup">
      <primitive
        v-for="pointObj in processPcd(pointArray)"
        :key="pointObj.annotation.id"
        :object="pointObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup v-if="showTypes?.pcd" ref="pointGroup">
      <primitive
        v-for="pointObj in processPcd(points)"
        :key="pointObj.annotation.id"
        :object="pointObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup v-if="showTypes?.lidar" :key="`lidar-${cameraType}`" ref="lidarGroup">
      <primitive
        v-for="lidarObj in processLidar(lidar)"
        :key="lidarObj.annotation.id"
        :object="lidarObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup
      v-if="showTypes?.imu"
      :position="[0, 0, 0]"
      :rotation="poseRotation"
    >
      <primitive :object="poseFrustumMesh" />
      <primitive :object="poseFrustumEdges" />
      <primitive :object="poseCameraFaceOutline" />
      <primitive :object="poseCameraTopMarker" />
      <TresMesh :position="[0, 0, 0]">
        <TresSphereGeometry :args="[0.05, 16, 16]" />
        <TresMeshStandardMaterial color="#FFFFFF" :metalness="0.15" :roughness="0.45" />
      </TresMesh>
    </TresGroup>

    <TresGroup
      v-if="showTypes?.['3dbox']"
      ref="boxGroup"
      :invalidateBoxes="invalidateBoxes"
    >
      <TresMesh
        v-for="(box, boxIndex) in boxes3d"
        :ref="(object) => boxRefsFn(object, boxIndex, box)"
        :key="box.id"
        :position="[box.data_json.x, box.data_json.y, box.data_json.z]"
        @click="(el, intersection, pointerEvent) => handleBoxClicked(box, el, intersection, pointerEvent)"
        @pointer-enter="(el) => handleBoxPointerEnter(el)"
        @pointer-leave="(object) => handleBoxPointerLeave(object)"
      >
        <TresBoxGeometry :args="[box.data_json.dx, box.data_json.dy, box.data_json.dz, 1, 1, 1]" />
        <primitive v-if="!invalidateBoxes" :object="getBoxTopEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, isSelectedBox(box) ? SELECTED_EDGE_WIDTH : 0.003, isUnsavedBox(box), isSelectedBox(box))" />
        <primitive v-if="!invalidateBoxes" :object="getBoxBottomEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, isSelectedBox(box) ? SELECTED_EDGE_WIDTH : 0.003, isUnsavedBox(box), isSelectedBox(box))" />
        <primitive v-if="!invalidateBoxes" :object="getBoxLeftEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, isSelectedBox(box) ? SELECTED_EDGE_WIDTH : 0.003, isUnsavedBox(box), isSelectedBox(box))" />
        <primitive v-if="!invalidateBoxes" :object="getBoxRightEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, isSelectedBox(box) ? SELECTED_EDGE_WIDTH : 0.003, isUnsavedBox(box), isSelectedBox(box))" />
        <TresLineDashedMaterial
          :color="isSelectedBox(box) ? SELECTED_BOX_COLOR : getLabelColor(box.label_index)"
          :wireframe="false"
          :transparent="true"
          :opacity="getBoxFillOpacity(box)"
        />
      </TresMesh>
      <TresMesh
        v-if="dragPreviewBox"
        :position="[dragPreviewBox.data_json.x, dragPreviewBox.data_json.y, dragPreviewBox.data_json.z]"
      >
        <TresBoxGeometry :args="[dragPreviewBox.data_json.dx, dragPreviewBox.data_json.dy, dragPreviewBox.data_json.dz, 1, 1, 1]" />
        <primitive :object="getBoxTopEdges(dragPreviewBox.data_json.dx, dragPreviewBox.data_json.dy, dragPreviewBox.data_json.dz, dragPreviewBox.label_index, 0.003, true)" />
        <primitive :object="getBoxBottomEdges(dragPreviewBox.data_json.dx, dragPreviewBox.data_json.dy, dragPreviewBox.data_json.dz, dragPreviewBox.label_index, 0.003, true)" />
        <primitive :object="getBoxLeftEdges(dragPreviewBox.data_json.dx, dragPreviewBox.data_json.dy, dragPreviewBox.data_json.dz, dragPreviewBox.label_index, 0.003, true)" />
        <primitive :object="getBoxRightEdges(dragPreviewBox.data_json.dx, dragPreviewBox.data_json.dy, dragPreviewBox.data_json.dz, dragPreviewBox.label_index, 0.003, true)" />
        <TresLineDashedMaterial
          :color="getLabelColor(dragPreviewBox.label_index)"
          :wireframe="false"
          :transparent="true"
          :opacity="0.15"
        />
      </TresMesh>
    </TresGroup>
    <TresAmbientLight :intensity="1" />
    <TresGridHelper
      ref="grid"
      :args="[100, 100]"
      :rotateX="Math.PI/2"
    />
    <TresAxesHelper :args="[10]" />
    <primitive :object="axisMarkers" />
  </TresCanvas>
  <div
    v-if="clusterSelectionStyle"
    class="cluster-selection"
    :style="clusterSelectionStyle"
  />
</template>

<script setup>
import AnnotationToolViewingMode3DControls from '@/components/DatasetComponent/AnnotationTool/AnnotationToolViewingMode3DControls.vue';
import { TresCanvas } from '@tresjs/core';
import { OrbitControls, TransformControls } from '@tresjs/cientos';
import {
  ref, reactive, shallowRef, computed, onMounted, onUnmounted, watch, toRefs, nextTick,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import usePCD from '@/composables/annotationTool/usePCD.js';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import * as THREE from 'three';
import gsap from 'gsap';
import { v4 as uuidv4 } from 'uuid';
import { getAnnotationTypes, isAnnotationTypeHidden } from '@/assets/js/utils.js';
import { CLUSTER_PARAMS, findLargestCluster, computeBoundingBox } from '@/utils/pointCloudClustering.js';
import { useToast } from 'vue-toast-notification';
import useColorParser from '@/composables/useColorParser.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

const props = defineProps({
  imageObj: { type: Object, default: () => {} },
  annotations: { type: Array, default: () => [] },
  pendingAnnotations: { type: Array, default: () => [] },
  updatedAnnotations: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  selectedLabel: { type: Object, default: null },
  selectedLabels: { type: Object, default: null },
  selectedAnnotationSets: { type: Array, default: () => [] },
  hiddenAnnotationIdentifiers: { type: Array, default: () => [] },
  isAuditMode: { type: Boolean, default: false },
  points: { type: Array, default: null },
  lidar: { type: Array, default: null },
  currentTool: { type: String, default: null },
  selectedAnnotationIdentifiers: { type: Array, default: () => [] },
  showTypes: { type: Object, default: () => {} },
  editingEnabled: { type: Boolean, default: false },
});
const {
  imageObj,
  annotations,
  pendingAnnotations,
  labels,
  selectedLabel,
  selectedLabels,
  selectedAnnotationSets,
  hiddenAnnotationIdentifiers,
  isAuditMode,
  points,
  lidar,
  currentTool,
  selectedAnnotationIdentifiers,
  showTypes,
  editingEnabled,
} = toRefs(props);

const emit = defineEmits(['update:updatedAnnotations', 'create-annotation', 'update:selectedAnnotationIdentifiers']);

const SELECTED_BOX_OPACITY = 0.65;
const SELECTED_BOX_COLOR = '#FFFFFF';
const SELECTED_EDGE_WIDTH = 0.007;

const {
  pointArray,
  pcdAnnotations,
  parsePCDFileAsPointsBuffer,
  colorPointsByXValue,
  setPointsMaterial,
  cloneLidarPointsBuffer,
  clonePcdPointsBuffer,
} = usePCD();

const {
  parseColor,
} = useColorParser();

const gl = reactive({
  clearColor: '#2B3846',
  shadows: true,
  alpha: false,
  shadowMapType: THREE.BasicShadowMap,
  outputEncoding: THREE.SRGBColorSpace,
  toneMapping: THREE.NoToneMapping,
});

const canvas = ref(null);
const grid = ref(null);
const cameraPerspective = shallowRef(null);
const cameraOrthographic = shallowRef(null);
const controls = ref(null);
const transformControls = ref(null);
const scene = ref(null);
const renderer = ref(null);
const raycaster = ref(null);
const cameraType = ref('perspective');
const cameraPerspectivePosition = ref([-5, 5, 5]);
const cameraOrthographicPosition = ref([-10, 0, 0]);
const upDirection = [0, 0, 1];
const lookAt = ref([0, 0, 0]);
const axisMarkers = new THREE.Group();
const axisLabelTextures = [];

function addAxisLabel(text, position, color) {
  const labelCanvas = document.createElement('canvas');
  labelCanvas.width = 128;
  labelCanvas.height = 64;
  const context = labelCanvas.getContext('2d');
  if (!context) return;

  context.fillStyle = 'rgba(0, 0, 0, 0.7)';
  context.fillRect(0, 0, labelCanvas.width, labelCanvas.height);
  context.font = 'bold 40px sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillStyle = color;
  context.fillText(text, 64, 32);

  const texture = new THREE.CanvasTexture(labelCanvas);
  axisLabelTextures.push(texture);
  const material = new THREE.SpriteMaterial({
    map: texture,
    sizeAttenuation: false,
    depthTest: false,
    depthWrite: false,
  });
  const label = new THREE.Sprite(material);
  label.position.copy(position);
  label.scale.set(0.032, 0.016, 1);
  label.onBeforeRender = (webGLRenderer, currentScene, camera) => {
    const scale = camera.isOrthographicCamera
      ? 1 / (Math.tan(THREE.MathUtils.degToRad(45 / 2)) * camera.projectionMatrix.elements[5])
      : 1;
    label.scale.set(0.032 * scale, 0.016 * scale, 1);
    label.updateMatrixWorld();
  };
  label.renderOrder = 10;
  label.raycast = () => {};
  axisMarkers.add(label);
}

[
  { name: 'X', direction: new THREE.Vector3(1, 0, 0), offset: new THREE.Vector3(0, 0, 0.25), color: '#ff6666' },
  { name: 'Y', direction: new THREE.Vector3(0, 1, 0), offset: new THREE.Vector3(0, 0, 0.25), color: '#66ff66' },
  { name: 'Z', direction: new THREE.Vector3(0, 0, 1), offset: new THREE.Vector3(0.25, 0, 0), color: '#6699ff' },
].forEach((axis) => {
  const tickPositions = [];
  for (let meter = 1; meter <= 10; meter += 1) {
    const position = axis.direction.clone().multiplyScalar(meter);
    addAxisLabel(`${meter} m`, position, axis.color);
    tickPositions.push(
      ...position.clone().addScaledVector(axis.offset, -0.3).toArray(),
      ...position.clone().addScaledVector(axis.offset, 0.3).toArray(),
    );
  }
  addAxisLabel(axis.name, axis.direction.clone().multiplyScalar(10.75), axis.color);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(tickPositions, 3));
  const ticks = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: axis.color }));
  ticks.raycast = () => {};
  axisMarkers.add(ticks);
});

const transformObject = ref(null);
const transformMode = ref('translate');
const transformEnabled = ref(false);
const selectedObjects = ref({
  boxes: [],
});
const boxDragStart = ref(null);
const boxDragDistance = ref(0);

const $toast = useToast();
const MIN_CLUSTER_REGION_PX = 5;
const MIN_CLUSTER_BOX_SIZE = 0.1;
const clusterDragStart = ref(null);
const clusterClickStart = ref(null);
const clusterDragEnd = ref(null);
const clusterSelectionStyle = computed(() => {
  if (!clusterDragStart.value || !clusterDragEnd.value) return null;
  const { clientX: startX, clientY: startY } = clusterDragStart.value;
  const { clientX: endX, clientY: endY } = clusterDragEnd.value;
  return {
    left: `${Math.min(startX, endX)}px`,
    top: `${Math.min(startY, endY)}px`,
    width: `${Math.abs(endX - startX)}px`,
    height: `${Math.abs(endY - startY)}px`,
  };
});

const poseFrustumDepth = 0.55;
const poseFrustumHalfWidth = 0.2;
const poseFrustumHalfHeight = 0.13;

const poseFrustumGeometry = new THREE.BufferGeometry();
poseFrustumGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
  0, 0, 0,
  poseFrustumDepth, -poseFrustumHalfWidth, poseFrustumHalfHeight,
  poseFrustumDepth, poseFrustumHalfWidth, poseFrustumHalfHeight,
  poseFrustumDepth, poseFrustumHalfWidth, -poseFrustumHalfHeight,
  poseFrustumDepth, -poseFrustumHalfWidth, -poseFrustumHalfHeight,
], 3));
poseFrustumGeometry.setIndex([
  0, 1, 2,
  0, 2, 3,
  0, 3, 4,
  0, 4, 1,
  1, 4, 3,
  1, 3, 2,
]);
poseFrustumGeometry.computeVertexNormals();

const poseFrustumMesh = shallowRef(null);

const poseFrustumEdges = shallowRef(null);

const poseCameraFaceOutline = shallowRef(null);

const poseTopMarkerHalfBase = 0.045;
const poseTopMarkerHeight = 0.075;
const poseTopMarkerX = poseFrustumDepth + 0.001;
const poseTopMarkerGeometry = new THREE.BufferGeometry();
poseTopMarkerGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
  poseTopMarkerX, -poseTopMarkerHalfBase, poseFrustumHalfHeight,
  poseTopMarkerX, poseTopMarkerHalfBase, poseFrustumHalfHeight,
  poseTopMarkerX, 0, poseFrustumHalfHeight + poseTopMarkerHeight,
], 3));
poseTopMarkerGeometry.setIndex([0, 1, 2]);
poseTopMarkerGeometry.computeVertexNormals();

const poseCameraTopMarker = shallowRef(null);

function rebuildPoseObjects() {
  poseFrustumMesh.value = new THREE.Mesh(
    poseFrustumGeometry,
    new THREE.MeshStandardMaterial({
      color: 0x5BC0EB,
      metalness: 0.1,
      roughness: 0.65,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
    }),
  );

  poseFrustumEdges.value = new THREE.LineSegments(
    new THREE.EdgesGeometry(poseFrustumGeometry),
    new THREE.LineBasicMaterial({ color: 0xFFFFFF }),
  );

  poseCameraFaceOutline.value = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(poseFrustumDepth, -poseFrustumHalfWidth, poseFrustumHalfHeight),
      new THREE.Vector3(poseFrustumDepth, poseFrustumHalfWidth, poseFrustumHalfHeight),
      new THREE.Vector3(poseFrustumDepth, poseFrustumHalfWidth, -poseFrustumHalfHeight),
      new THREE.Vector3(poseFrustumDepth, -poseFrustumHalfWidth, -poseFrustumHalfHeight),
    ]),
    new THREE.LineBasicMaterial({ color: 0xFFD166 }),
  );

  poseCameraTopMarker.value = new THREE.Mesh(
    poseTopMarkerGeometry,
    new THREE.MeshBasicMaterial({
      color: 0xFFD166,
      side: THREE.DoubleSide,
    }),
  );
}

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePayload(value) {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }
  return typeof value === 'object' ? value : null;
}

function isImuLikeAnnotation(annotation) {
  const type = String(annotation?.type || '').toLowerCase();
  const topic = String(annotation?.topic || annotation?.topic_name || annotation?.source || '').toLowerCase();
  return type === 'imu' || type.startsWith('imu_') || topic === 'imu' || topic.startsWith('imu_');
}

function getAngleFromPayload(payload, keys) {
  if (!payload) {
    return null;
  }
  for (const key of keys) {
    const value = toFiniteNumber(payload?.[key]);
    if (value !== null) {
      return value;
    }
  }
  return null;
}

function toRadians(angle) {
  if (angle === null) {
    return null;
  }
  return THREE.MathUtils.degToRad(angle);
}

function extractPoseEuler(annotation) {
  const payloads = [
    normalizePayload(annotation?.data_json),
    normalizePayload(annotation?.raw),
    normalizePayload(annotation),
  ].filter(Boolean);

  for (const payload of payloads) {
    const orientation = normalizePayload(payload?.orientation);
    const euler = normalizePayload(payload?.euler);
    const source = euler || orientation || payload;

    const roll = getAngleFromPayload(source, ['roll', 'x', 'phi']);
    const pitch = getAngleFromPayload(source, ['pitch', 'y', 'theta']);
    const yaw = getAngleFromPayload(source, ['yaw', 'z', 'psi']);

    if (roll !== null || pitch !== null || yaw !== null) {
      const rollValue = roll || 0;
      const pitchValue = pitch || 0;
      const yawValue = yaw || 0;

      return {
        yaw: toRadians(yawValue),
        pitch: toRadians(pitchValue),
        roll: toRadians(rollValue),
      };
    }
  }

  return null;
}

const poseEuler = computed(() => {
  const imuAnnotation = (annotations.value || []).find((annotation) => isImuLikeAnnotation(annotation));
  if (!imuAnnotation) {
    return null;
  }
  return extractPoseEuler(imuAnnotation);
});

const poseRotation = computed(() => {
  if (!poseEuler.value) {
    return [0, 0, 0];
  }
  // YAW is negated to match the expected rotation direction in the 3D viewer.
  const rotation = [poseEuler.value.roll, poseEuler.value.pitch, poseEuler.value.yaw];
  // [Debug]
  // console.log('[PointCloudViewer] poseRotation (roll, pitch, yaw):', rotation, {
  //   roll: poseEuler.value.roll,
  //   pitch: poseEuler.value.pitch,
  //   yaw: poseEuler.value.yaw,
  // });
  return rotation;
});

const boxRefs = shallowRef([]);
const boxRefsFn = (object, boxIndex, box) => {
  if (object) {
    object.userData.annotation = box;
    boxRefs.value[boxIndex] = object;
  } else {
    boxRefs.value.splice(boxIndex, 1);
  }
};
const invalidateBoxes = ref(false);
const hoveredBoxId = ref(null);

const currentCamera = computed(() => {
  if (cameraType.value === 'perspective') {
    return cameraPerspective.value;
  } else if (cameraType.value === 'orthographic') {
    return cameraOrthographic.value;
  }
  return cameraPerspective.value;
});

function requestCanvasRender(reason) {
  nextTick(() => {
    const context = canvas.value?.context;
    if (typeof context?.invalidate === 'function') {
      context.invalidate();
    }
    if (typeof context?.advance === 'function') {
      context.advance();
    }
    if (renderer.value && scene.value && currentCamera.value) {
      renderer.value.render(scene.value, currentCamera.value);
    }
  });
}

const boxes3d = computed(() => [
  ...annotations.value.filter((anno) => {
    const isLabelSelected = !selectedLabels.value || selectedLabels.value?.includes(anno.label_index);
    const isAnnotationSetSelected = !selectedAnnotationSets.value || selectedAnnotationSets.value?.includes(anno.annotation_set_id);
    return getAnnotationTypes(anno).includes('box3d') && isLabelSelected && (isAnnotationSetSelected || isAuditMode.value) && !isAnnotationTypeHidden(hiddenAnnotationIdentifiers.value, anno.id, '3dbox');
  }),
  ...pendingAnnotations.value.filter((anno) => getAnnotationTypes(anno).includes('box3d') && anno.image_id === imageObj.value?.id),
]);

const dragPreviewBox = computed(() => {
  if (!boxDragStart.value || !selectedLabel.value) return null;
  if (boxDragDistance.value < 4) return null;

  const height = Math.max(0.1, boxDragDistance.value * 0.01);
  return create3DBoxAnnotation(boxDragStart.value.point, height, 'preview-3d-box');
});

function processLidar(lidarToProcess) {
  if (!lidarToProcess) return [];

  return lidarToProcess.map((item) => {
    // Create a fresh copy of the lidar data
    const clonedItem = {
      annotation: item.annotation,
      // Make a deep copy of the original data
      pointsBuffer: cloneLidarPointsBuffer(item.positions, item.colors),
    };
    colorPointsByXValue(clonedItem.pointsBuffer);
    if (cameraType.value === 'perspective') {
      clonedItem.pointsBuffer.material.size = .1;
      clonedItem.pointsBuffer.material.needsUpdate = true;
    } else {
      clonedItem.pointsBuffer.material.size = 2;
      clonedItem.pointsBuffer.material.sizeAttenuation = false;
      clonedItem.pointsBuffer.material.needsUpdate = true;
    }
    return clonedItem;
  });
}

// Add a computed property to create fresh copies of pcd data. Needed because tres.js clears the geometry when disposed.
// Remove if tres version updated
function processPcd(pcdToProcess) {
  if (!pcdToProcess) return [];

  return pcdToProcess.map((item) => {
    // Create a fresh copy of the points data
    const clonedItem = {
      annotation: item.annotation,
      // Make a deep copy of the original data
      pointsBuffer: clonePcdPointsBuffer(item.positions, item.colors),
    };
    return clonedItem;
  });
}

watch(labels, () => {
  deselectAllBoxes();
  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
});

watch(() => showTypes.value?.imu, (imuVisible) => {
  if (imuVisible) {
    rebuildPoseObjects();
  }
  requestCanvasRender('showTypes.imu');
}, { immediate: true });

watch(poseEuler, (value) => {
  if (showTypes.value?.imu && value) {
    rebuildPoseObjects();
  }
  requestCanvasRender('poseEuler');
}, { deep: true });

watch(annotations, async (newAnnotations) => {
  if (newAnnotations) {
    pcdAnnotations.value = newAnnotations.filter((anno) => anno.type === "pcd");
  }
});

watch(pcdAnnotations, async () => {
  getPointArray();
});

watch(currentTool, async (newTool) => {
  if (!editingEnabled.value) return;

  if (['translate-object', 'scale-object'].includes(currentTool.value)) {
    if (transformEnabled.value && transformObject.value) {
      if (currentTool.value === 'translate-object') transformMode.value = 'translate';
      if (currentTool.value === 'scale-object') transformMode.value = 'scale';
    }
    if (selectedObjects.value.boxes.length > 0) {
      const selectedBox = boxRefs.value.find((boxRef) => boxRef.userData.selected);
      if (selectedBox) {
        transformObject.value = selectedBox;
        transformEnabled.value = true;
      }
    }
  } else {
    transformEnabled.value = false;
    transformObject.value = null;
  }
});

watch(currentTool, () => {
  cancelClusterSelection();
});

watch(imageObj, (newImageObj) => {
  transformEnabled.value = false;
  transformObject.value = null;
});

watch(selectedAnnotationIdentifiers, () => {
  // Derive local 3D box selection from the shared cross-viewer selection list (2D canvas ids included).
  selectedObjects.value.boxes = boxes3d.value.filter((box) => selectedAnnotationIdentifiers.value.includes(box.id));
  updateBoxSelection();
}, { immediate: true });

onMounted(async () => {
  window.addEventListener('keyup', handleKeyEvent);

  renderer.value = canvas.value.context.renderer.value;
  renderer.value.setPixelRatio(window.devicePixelRatio);
  scene.value = canvas.value.context.scene.value;
  raycaster.value = canvas.value.context.raycaster.value;
  rebuildPoseObjects();
  pcdAnnotations.value = annotations.value.filter((anno) => anno.type === "pcd");
  getPointArray();

  enableCanvasClick();
  enableCanvasPointerEvents();
});

onUnmounted(() => {
  window.removeEventListener('keyup', handleKeyEvent);
  disableCanvasClick();
  disableCanvasPointerEvents();
  axisMarkers.traverse((object) => {
    object.geometry?.dispose();
    object.material?.dispose();
  });
  axisLabelTextures.forEach((texture) => texture.dispose());
});

function handleKeyEvent(event) {
  if (event.key === 'Delete') {
    if (!editingEnabled.value) return;

    deleteObjects();
  }
}

async function readFile(annotation) {
  const dataConnect = new DatastoreConnect();
  let data;
  if (annotation.is_sensor) {
    data = await dataConnect.getSensorFile(annotation.id, 'arrayBuffer')
      .catch((error) => {
        console.log(error);
        return null;
      });
  } else {
    data = await dataConnect.getAnnotationFile(annotation.id, 'arrayBuffer')
      .catch((error) => {
        console.log(error);
        return null;
      });
  }
  return data;
}

async function getPointArray() {
  // Reset point array for each annotation
  pointArray.value = [];
  if (!points.value && pcdAnnotations.value.length > 0) {
    for (const annotation of pcdAnnotations.value) {
      if (annotation.polygon && annotation.polygon !== "") {
        // Convert polygon to ArrayBuffer
        const textEncoder = new TextEncoder();
        const arrayBuffer = textEncoder.encode(annotation.polygon).buffer;
        annotation.data = arrayBuffer;
        // Process points buffer and material
        const pointsBuffer = shallowRef(null);
        pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
        setPointsMaterial(pointsBuffer.value.material);
        // Add annotation and pointsBuffer to pointArray
        pointArray.value.push({
          annotation,
          pointsBuffer,
        });
      } else {
        const arrayBuffer = await readFile(annotation);
        if (arrayBuffer) {
          annotation.data = arrayBuffer;
          // Process points buffer and material
          const pointsBuffer = shallowRef(null);
          pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
          setPointsMaterial(pointsBuffer.value.material);
          // Add annotation and pointsBuffer to pointArray
          pointArray.value.push({
            annotation,
            pointsBuffer,
          });
        }
      }
    }
  } else {
    pointArray.value = [];
  }
}

function handleBoxClicked(box, el, intersection, pointerEvent) {
  if (currentTool.value === '3d-pointer') {
    if (el.object.geometry.type === 'BoxGeometry') {
      const isCtrlPressed = Boolean(pointerEvent?.ctrlKey || pointerEvent?.metaKey);
      if (isCtrlPressed) {
        if (selectedAnnotationIdentifiers.value.includes(box.id)) {
          deselectBox(box);
        } else {
          selectBox(el.object, box);
        }
      } else {
        deselectAllBoxes();
        selectBox(el.object, box);
      }
    }
  }

  if (['translate-object', 'scale-object'].includes(currentTool.value)) {
    if (!editingEnabled.value) return;

    if (currentTool.value === 'translate-object') transformMode.value = 'translate';
    if (currentTool.value === 'scale-object') transformMode.value = 'scale';

    deselectAllBoxes();
    selectBox(el.object, box);
    transformObject.value = el.object;
    transformEnabled.value = true;
  }

  if (currentTool.value === '3d-edit-label') {
    if (!editingEnabled.value) return;

    el.object.userData.annotation.label_index = selectedLabel.value.index;
    el.object.userData.annotation.label_id = selectedLabel.value.id;
    el.object.userData.annotation.label_name = selectedLabel.value.name;
    el.object.material.color.set(getLabelColor(selectedLabel.value?.index));
    const lineMaterial = el.object.children[0].material.clone();
    lineMaterial.color.set(getLabelColor(selectedLabel.value?.index));
    el.object.children[0].material = lineMaterial;
    emit('update:updatedAnnotations', JSON.parse(JSON.stringify(annotations.value)));
  }
}

// Handle point colors
const { map: labelColorMap } = useAnnotationColorMap({ items: labels, key: 'index' });
function getLabelColor(index) {
  if (index !== undefined && index !== null) {
    const targetLabel = labels.value.find((e) => e.index === index);
    if (targetLabel && targetLabel.color) {
      const parsed = parseColor(targetLabel.color);
      // Only strip an alpha suffix when present (#RRGGBBAA); leave plain #RRGGBB untouched.
      return parsed.length === 9 ? parsed.slice(0, -2) : parsed;
    } else if (labelColorMap.value[index]) {
      return labelColorMap.value[index];
    }
  }
  return '#250E81';
}

function getLineMaterial(label, lineWidth, dashed = false, highlight = false) {
  return new LineMaterial({
    color: highlight ? SELECTED_BOX_COLOR : getLabelColor(label),
    linewidth: lineWidth,
    dashed,
    dashSize: 0.04,
    gapSize: 0.04,
  });
}

function getEdgeLine(vertices, label, lineWidth, dashed = false, highlight = false) {
  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(vertices.flat());

  const line = new Line2(lineGeometry, getLineMaterial(label, lineWidth, dashed, highlight));
  if (dashed) {
    line.computeLineDistances();
  }
  return line;
}

function isUnsavedBox(box) {
  return box?.pending || box?.save_pending || box?.status === 'new';
}

function isSelectedBox(box) {
  return selectedAnnotationIdentifiers.value.includes(box.id);
}

function getBoxFillOpacity(box) {
  if (isSelectedBox(box)) return SELECTED_BOX_OPACITY;
  return hoveredBoxId.value === box.id ? 0.375 : 0.25;
}

function getBoxTopEdges(dx, dy, dz, label, lineWidth = 0.001, dashed = false, highlight = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, dz / 2],
    [dx / 2, -dy / 2, dz / 2],
    [dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, dz / 2],
    [-dx / 2, -dy / 2, dz / 2],
  ];

  return getEdgeLine(vertices, label, lineWidth, dashed, highlight);
}

function getBoxBottomEdges(dx, dy, dz, label, lineWidth = 0.001, dashed = false, highlight = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, -dz / 2],
    [dx / 2, dy / 2, -dz / 2],
    [-dx / 2, dy / 2, -dz / 2],
    [-dx / 2, -dy / 2, -dz / 2],
  ];

  return getEdgeLine(vertices, label, lineWidth, dashed, highlight);
}

function getBoxLeftEdges(dx, dy, dz, label, lineWidth = 0.001, dashed = false, highlight = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, dy / 2, -dz / 2],
    [dx / 2, dy / 2, -dz / 2],
    [dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, -dz / 2],
  ];

  return getEdgeLine(vertices, label, lineWidth, dashed, highlight);
}

function getBoxRightEdges(dx, dy, dz, label, lineWidth = 0.001, dashed = false, highlight = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, dz / 2],
    [-dx / 2, -dy / 2, dz / 2],
    [-dx / 2, -dy / 2, -dz / 2],
  ];

  return getEdgeLine(vertices, label, lineWidth, dashed, highlight);
}

async function handleTopView() {
  controls.value.value.enableDamping = false;
  controls.value.value.enabled = false;
  gsap.to(currentCamera.value.position, {
    duration: 1,
    x: 0,
    y: 0,
    z: 20,
    ease: 'power3.out',
    onComplete: () => {
      controls.value.value.enableDamping = true;
      controls.value.value.enabled = true;
    },
  });
  gsap.to(currentCamera.value.up, {
    delay: 0.1,
    duration: 1,
    x: 1,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
  gsap.to(controls.value.value.target, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
}

async function handleFrontView() {
  controls.value.value.enableDamping = false;
  controls.value.value.enabled = false;
  gsap.to(currentCamera.value.position, {
    delay: 0.1,
    duration: 1,
    x: -10,
    y: 0,
    z: 0,
    ease: 'power3.out',
    onComplete: () => {
      controls.value.value.enableDamping = true;
      controls.value.value.enabled = true;
    },
  });
  gsap.to(currentCamera.value.up, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 1,
    ease: 'power3.out',
  });
  gsap.to(controls.value.value.target, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
}

async function switchToPerspectiveCamera() {
  const currentUp = cameraOrthographic.value.up.clone();
  cameraOrthographicPosition.value = cameraOrthographic.value.position.clone();
  cameraPerspectivePosition.value = cameraOrthographicPosition.value;
  cameraPerspectivePosition.value = cameraOrthographicPosition.value;

  // Switch camera type
  cameraType.value = 'perspective';
  await nextTick();

  cameraPerspective.value.up = currentUp;
  cameraPerspective.value.updateProjectionMatrix();

  controls.value.value.object = cameraPerspective.value;
  controls.value.value.update();
}

async function switchToOrthographicCamera() {
  const currentUp = cameraPerspective.value.up.clone();
  cameraPerspectivePosition.value = cameraPerspective.value.position.clone();
  cameraOrthographicPosition.value = cameraPerspectivePosition.value;
  // Get matching perspective to orthographic zoom
  const distance = cameraPerspective.value.position.distanceTo(controls.value.value.target);
  const halfWidth = frustumWidthAtDistance(cameraPerspective.value, distance) / 2;
  const halfHeight = frustumHeightAtDistance(cameraPerspective.value, distance) / 2;

  // Switch camera type
  cameraType.value = 'orthographic';
  await nextTick();

  // Set matching perspective to orthographic zoom
  cameraOrthographic.value.zoom = 1;
  cameraOrthographic.value.top = halfHeight;
  cameraOrthographic.value.bottom = -halfHeight;
  cameraOrthographic.value.left = -halfWidth;
  cameraOrthographic.value.right = halfWidth;
  cameraOrthographic.value.up = currentUp;
  cameraOrthographic.value.updateProjectionMatrix();

  controls.value.value.object = cameraOrthographic.value;
  controls.value.value.update();
}

function frustumHeightAtDistance(camera, distance) {
  const vFov = (camera.fov * Math.PI) / 180;
  return Math.tan(vFov / 2) * distance * 2;
}

function frustumWidthAtDistance(camera, distance) {
  return frustumHeightAtDistance(camera, distance) * camera.aspect;
}

async function handleTransformMouseUp(object) {
  const newAnnotations = JSON.parse(JSON.stringify(annotations.value));
  const transformAnnotation = newAnnotations.find((anno) => anno.id === object.userData.annotation.id);

  transformAnnotation.data_json.x = object.position.x;
  transformAnnotation.data_json.y = object.position.y;
  transformAnnotation.data_json.z = object.position.z;
  transformAnnotation.data_json.dx *= object.scale.x;
  transformAnnotation.data_json.dy *= object.scale.y;
  transformAnnotation.data_json.dz *= object.scale.z;
  object.scale.set(1, 1, 1);

  emit('update:updatedAnnotations', JSON.parse(JSON.stringify(newAnnotations)));

  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

function handleCanvasClick(event) {
  if (currentTool.value === '3d-pointer') {
    // Deselect currently selected boxes
    const intersects = raycaster.value.intersectObjects(scene.value.children);
    const boxIntersects = intersects.filter((i) => i.object.type === 'Mesh' && i.object.geometry.type === 'BoxGeometry');
    if (boxIntersects.length === 0) {
      deselectAllBoxes();
    }
  }
}

function selectBoxAtPointer(event) {
  if (!currentCamera.value || !renderer.value || !scene.value) return false;

  const rect = renderer.value.domElement.getBoundingClientRect();
  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -(((event.clientY - rect.top) / rect.height) * 2 - 1),
  );
  raycaster.value.setFromCamera(pointer, currentCamera.value);
  const intersects = raycaster.value.intersectObjects(scene.value.children);
  const boxIntersect = intersects.find((i) => i.object.type === 'Mesh' && i.object.geometry.type === 'BoxGeometry' && i.object.userData?.annotation);

  if (!boxIntersect) {
    if (!(event.ctrlKey || event.metaKey)) deselectAllBoxes();
    return false;
  }

  if (event.ctrlKey || event.metaKey) {
    if (selectedAnnotationIdentifiers.value.includes(boxIntersect.object.userData.annotation.id)) {
      deselectBox(boxIntersect.object.userData.annotation);
    } else {
      selectBox(boxIntersect.object, boxIntersect.object.userData.annotation);
    }
    return true;
  }

  deselectAllBoxes();
  selectBox(boxIntersect.object, boxIntersect.object.userData.annotation);
  return true;
}

function getGridIntersectionPoint(event) {
  if (!currentCamera.value || !renderer.value || !scene.value) return null;

  const rect = renderer.value.domElement.getBoundingClientRect();
  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -(((event.clientY - rect.top) / rect.height) * 2 - 1),
  );
  raycaster.value.setFromCamera(pointer, currentCamera.value);
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const point = new THREE.Vector3();
  return raycaster.value.ray.intersectPlane(groundPlane, point);
}

function handleCanvasPointerDown(event) {
  if (event.button !== 0) return;
  if (currentTool.value === '3d-cluster') {
    clusterClickStart.value = { clientX: event.clientX, clientY: event.clientY };
    startClusterSelection(event);
    return;
  }
  if (currentTool.value !== '3d-box' || !editingEnabled.value || !selectedLabel.value) return;

  const gridPoint = getGridIntersectionPoint(event);
  if (!gridPoint) return;

  boxDragStart.value = {
    clientX: event.clientX,
    clientY: event.clientY,
    point: gridPoint.clone(),
  };
  boxDragDistance.value = 0;
  controls.value.value.enabled = false;
}

function resetBoxDrag() {
  boxDragStart.value = null;
  boxDragDistance.value = 0;
  clusterClickStart.value = null;
  cancelClusterSelection();
  if (controls.value?.value) {
    controls.value.value.enabled = true;
  }
}

function startClusterSelection(event) {
  if (!editingEnabled.value || !selectedLabel.value) return;

  clusterDragStart.value = { clientX: event.clientX, clientY: event.clientY };
  clusterDragEnd.value = { clientX: event.clientX, clientY: event.clientY };
  controls.value.value.enabled = false;
}

function cancelClusterSelection() {
  if (!clusterDragStart.value) return;
  clusterDragStart.value = null;
  clusterDragEnd.value = null;
  if (controls.value?.value) {
    controls.value.value.enabled = true;
  }
}

function getPositionsOfItem(item) {
  const pointsBuffer = item?.pointsBuffer?.value ?? item?.pointsBuffer;
  return item?.positions ?? pointsBuffer?.geometry?.attributes?.position?.array ?? null;
}

// LiDAR is preferred; Radar PCD is the fallback when no LiDAR is shown.
function getClusterSource() {
  const lidarPositions = showTypes.value?.lidar ? (lidar.value || []).map(getPositionsOfItem).filter(Boolean) : [];
  if (lidarPositions.length > 0) {
    return { positionsList: lidarPositions, params: CLUSTER_PARAMS.lidar };
  }
  const radarItems = showTypes.value?.pcd ? [...(points.value || []), ...(pointArray.value || [])] : [];
  const radarPositions = radarItems.map(getPositionsOfItem).filter(Boolean);
  if (radarPositions.length > 0) {
    return { positionsList: radarPositions, params: CLUSTER_PARAMS.radar };
  }
  return null;
}

function collectPointsInRegion(positionsList, ndcBounds) {
  const camera = currentCamera.value;
  camera.updateMatrixWorld();
  const projected = new THREE.Vector3();
  const selected = [];
  positionsList.forEach((positions) => {
    for (let i = 0; i + 2 < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];
      if (Number.isFinite(x + y + z)) {
        projected.set(x, y, z).project(camera);
        if (projected.z >= -1 && projected.z <= 1
          && projected.x >= ndcBounds.minX && projected.x <= ndcBounds.maxX
          && projected.y >= ndcBounds.minY && projected.y <= ndcBounds.maxY) {
          selected.push(x, y, z);
        }
      }
    }
  });
  return Float32Array.from(selected);
}

function showClusterWarning(message) {
  $toast.open({
    queue: true,
    message,
    type: 'warning',
    position: 'top',
    dismissible: true,
  });
}

function createClusterBox(start, end) {
  if (!currentCamera.value || !renderer.value) return;

  const source = getClusterSource();
  if (!source) {
    showClusterWarning('No visible LiDAR or Radar point cloud to cluster');
    return;
  }

  const rect = renderer.value.domElement.getBoundingClientRect();
  const toNdcX = (clientX) => ((clientX - rect.left) / rect.width) * 2 - 1;
  const toNdcY = (clientY) => -(((clientY - rect.top) / rect.height) * 2 - 1);
  const selected = collectPointsInRegion(source.positionsList, {
    minX: Math.min(toNdcX(start.clientX), toNdcX(end.clientX)),
    maxX: Math.max(toNdcX(start.clientX), toNdcX(end.clientX)),
    minY: Math.min(toNdcY(start.clientY), toNdcY(end.clientY)),
    maxY: Math.max(toNdcY(start.clientY), toNdcY(end.clientY)),
  });

  const cluster = findLargestCluster(selected, source.params);
  if (!cluster) {
    showClusterWarning('No cluster found in the selected region');
    return;
  }

  const { center, size } = computeBoundingBox(selected, cluster);
  const [dx, dy, dz] = size.map((value) => Math.max(value, MIN_CLUSTER_BOX_SIZE));
  emit('create-annotation', create3DBoxAnnotation({ x: center[0], y: center[1], z: center[2] }, dz, uuidv4(), { dx, dy }));

  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

function endClusterSelection(event) {
  const start = clusterDragStart.value;
  const end = { clientX: event.clientX, clientY: event.clientY };
  cancelClusterSelection();

  if (Math.abs(end.clientX - start.clientX) < MIN_CLUSTER_REGION_PX || Math.abs(end.clientY - start.clientY) < MIN_CLUSTER_REGION_PX) {
    return;
  }
  createClusterBox(start, end);
}

function handleCanvasPointerMove(event) {
  if (clusterDragStart.value) {
    if (event.buttons % 2 === 1) {
      clusterDragEnd.value = { clientX: event.clientX, clientY: event.clientY };
    } else {
      cancelClusterSelection();
    }
    return;
  }
  if (!boxDragStart.value) return;
  if (!(event.buttons & 1)) {
    resetBoxDrag();
    return;
  }

  const dx = event.clientX - boxDragStart.value.clientX;
  const dy = event.clientY - boxDragStart.value.clientY;
  boxDragDistance.value = Math.sqrt((dx * dx) + (dy * dy));
}

function create3DBoxAnnotation(point, height, id = uuidv4(), { dx = 0.5, dy = 0.5 } = {}) {
  return {
    id,
    label_name: selectedLabel.value.name,
    label_index: selectedLabel.value.index,
    label_id: selectedLabel.value.id,
    type: "3dbox",
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    score: 1,
    data_json: {
      dx,
      dy,
      dz: height,
      id: uuidv4(),
      label_name: selectedLabel.value.name,
      label_index: selectedLabel.value.index,
      x: point.x,
      y: point.y,
      z: point.z,
    },
    image_id: imageObj.value.id,
  };
}

function handleCanvasPointerUp(event) {
  if (currentTool.value === '3d-cluster' && event.button === 0 && clusterClickStart.value) {
    const click = clusterClickStart.value;
    clusterClickStart.value = null;
    const isClick = Math.abs(event.clientX - click.clientX) < MIN_CLUSTER_REGION_PX
      && Math.abs(event.clientY - click.clientY) < MIN_CLUSTER_REGION_PX;
    if (isClick) {
      cancelClusterSelection();
      selectBoxAtPointer(event);
      return;
    }
  }
  if (clusterDragStart.value && event.button === 0) {
    endClusterSelection(event);
    return;
  }
  if (event.button !== 0) {
    resetBoxDrag();
    return;
  }
  if (!boxDragStart.value) return;

  const start = boxDragStart.value;
  const dragDistance = boxDragDistance.value;
  resetBoxDrag();

  if (dragDistance < 4) {
    selectBoxAtPointer(event);
    return;
  }

  const height = Math.max(0.1, dragDistance * 0.01);
  const new3DBox = create3DBoxAnnotation(start.point, height);
  emit('create-annotation', new3DBox);

  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

function disableCanvasClick() {
  const canvasEl = renderer.value.domElement;
  canvasEl.removeEventListener('click', handleCanvasClick);
}

function enableCanvasClick() {
  const canvasEl = renderer.value.domElement;
  canvasEl.addEventListener('click', handleCanvasClick);
}

function disableCanvasPointerEvents() {
  const canvasEl = renderer.value.domElement;
  canvasEl.removeEventListener('pointerdown', handleCanvasPointerDown);
  canvasEl.removeEventListener('pointermove', handleCanvasPointerMove);
  canvasEl.removeEventListener('pointerup', handleCanvasPointerUp);
  canvasEl.removeEventListener('pointerleave', resetBoxDrag);
}

function enableCanvasPointerEvents() {
  const canvasEl = renderer.value.domElement;
  canvasEl.addEventListener('pointerdown', handleCanvasPointerDown);
  canvasEl.addEventListener('pointermove', handleCanvasPointerMove);
  canvasEl.addEventListener('pointerup', handleCanvasPointerUp);
  canvasEl.addEventListener('pointerleave', resetBoxDrag);
}

function handleBoxPointerEnter(el) {
  if (['3d-pointer', '3d-edit-label', 'translate-object', 'scale-object'].includes(currentTool.value) && !el.object.userData.selected) {
    hoveredBoxId.value = el.object.userData.annotation?.id ?? null;
  }
}

function handleBoxPointerLeave(object) {
  if (['3d-pointer', '3d-edit-label', 'translate-object', 'scale-object'].includes(currentTool.value) && !object.userData.selected) {
    hoveredBoxId.value = null;
  }
}

function selectBox(object, box) {
  if (selectedAnnotationIdentifiers.value.includes(box.id)) {
    return;
  }
  // Merge into the shared cross-viewer selection instead of replacing it, so a box picked here
  // can be joined together with items already selected in the 2D canvas.
  emit('update:selectedAnnotationIdentifiers', [...selectedAnnotationIdentifiers.value, box.id]);
  object.userData.selected = true;
}

function deselectBox(box) {
  emit('update:selectedAnnotationIdentifiers', selectedAnnotationIdentifiers.value.filter((id) => id !== box.id));
}

function deselectAllBoxes(object, box) {
  boxRefs.value.forEach((o) => {
    if (o.userData.selected) {
      o.userData.selected = false;
    }
  });
  // Clears the whole cross-viewer selection (matching the 2D canvas' empty-space click behavior).
  emit('update:selectedAnnotationIdentifiers', []);
}

function updateBoxSelection() {
  boxRefs.value.forEach((o) => {
    o.userData.selected = selectedAnnotationIdentifiers.value.includes(o.userData.annotation.id);
  });
}

function deleteObjects() {
  deleteSelectedBoxes(selectedObjects.value.boxes);
}

function deleteSelectedBoxes(selectedBoxes) {
  const updatedAnnotations = annotations.value.filter((box) => !selectedBoxes.map((selectedBox) => selectedBox.id).includes(box.id));
  emit('update:updatedAnnotations', JSON.parse(JSON.stringify(updatedAnnotations)));

  // Rerenders the boxes
  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

</script>

<style lang="scss" scoped>
.cluster-selection {
  position: fixed;
  border: 1px dashed #FFFFFF;
  background: rgba(255, 255, 255, 0.15);
  pointer-events: none;
  z-index: 11;
}
</style>
